<template>
    <div class="container-fluid content-wrapper">
        <div class="content-heading">{{ title }}</div>
        <b-row v-if="filters">
            <b-col cols="12" :sm="hasFilterSlot ? 9 : 12">
                <div v-if="addNewButton || $slots.actionButtons" class="action-buttons-slot d-flex flex-wrap align-items-center mb-3 gap-2">
                    <router-link
                        :to="addDestination()"
                        v-if="addNewButton"
                        class="btn btn-primary"
                    >
                        <b-icon-plus></b-icon-plus>
                        Add
                    </router-link>
                    <slot name="actionButtons">
                    </slot>
                </div>
                <b-row v-if="!hideSearch || $slots.search" class="search-filters">
                    <b-col class="ps-0 pe-0">
                        <slot name="search">
                            <b-form>
                                <b-form-group
                                    label-for="automatic"
                                    class="ms-3 mt-2 me-2"
                                >
                                    <b-form-row>
                                        <b-col class="d-flex align-items-center">
                                            <b-input-group>
                                                <b-form-input
                                                    size="lg"
                                                    name="automatic"
                                                    ref='searchRef'
                                                    type="search"
                                                    class="no-right-border"
                                                    v-model="filters.automatic"
                                                    :placeholder="
                                                        searchMessageComputed
                                                    "
                                                    debounce="300"
                                                    style="border-right: 0px;"
                                                    @keydown.enter.prevent=""
                                                />

                                                <span class="input-group-append-slot">
                                                    <slot name="appendSlot">
                                                    </slot>
                                                </span>
                                            </b-input-group>
                                            <div
                                                v-if="filterBy && filterBy.length > 1"
                                                class="ms-2 search-by-wrapper"
                                            >
                                                <p-select
                                                    set-invalid-value-to-first-option
                                                    class="search-filter search-by-select"
                                                    v-model="filters.searchBy"
                                                    :options="filterBy"
                                                />
                                            </div>
                                        </b-col>
                                        <slot name="afterSearch"> </slot>
                                    </b-form-row>
                                </b-form-group>
                            </b-form>
                        </slot>
                    </b-col>
                </b-row>
                <div v-if="$slots.belowSearch" class="below-search-slot">
                    <slot name="belowSearch"> </slot>
                </div>
                <div v-if="computedSelectionActions.length && showSetTo" class="set-to-slot d-flex align-items-center flex-wrap mb-2">
                    <b-dropdown
                        :disabled="selectedRows.length === 0"
                        text="Set To"
                        variant="outline-primary"
                        class="me-2"
                    >
                        <b-dropdown-item
                            v-for="selectionAction in computedSelectionActions"
                            :key="selectionAction.label"
                            @click="selectionAction.action"
                            :disabled="selectionAction.disabled"
                            v-show="!selectionAction.hidden"
                            >{{ selectionAction.label }}</b-dropdown-item
                        >
                    </b-dropdown>
                    <slot name="addButton"></slot>
                </div>

                <div>
                    <p-search-table
                        ref="searchTable"
                        v-model="selectedRows"
                        :include-search-input="false"
                        :sort-by="filters.sortBy"
                        :selectable="computedSelectionActions.length > 0 || !useBootstrapSelection"
                        :enable-row-selection="computedSelectionActions.length > 0 || !useBootstrapSelection"
                        :sort-desc="filters.sortDesc"
                        :api-destination="apiDestination"
                        :filters="filters"
                        :use-bootstrap-selection="useBootstrapSelection"
                        :search-destination="searchDestination"
                        :per-page="filters.perPage"
                        :show-top-paging="showTopPaging"
                        :show-bottom-paging="showBottomPaging"
                        :disable-initial-search="disableInitialSearch"
                        :component-is-loading="componentIsLoading"
                        :per-page-options="perPageOptions"
                        @row-selected="rowSelected"
                        @input="rowSelected"
                        @per-page-change="perPageUpdate"
                        @sort-by-change="sortByUpdate"
                        @sort-desc-change="sortDescUpdate"
                        @total-rows-changed="
                            $emit('total-rows-changed', $event)
                        "
                        @selected-rows-changed="$emit('selected-rows-changed', $event)"
                        :fields="allFields"
                        @is-table-loading="$emit('is-table-loading', $event)"
                        :submit-search-as-post="submitSearchAsPost"
                        :edit-key="editKey"
                    >
                        <template v-slot:head(edit)>
                            <span>{{ editButtonLabel }}</span>
                        </template>
                        <template
                            v-if="this.showEdit"
                            v-slot:cell(edit)="{ value, item }"
                        >
                            <router-link :to="editDestination(item)">
                                <b-button variant="outline-primary" size="sm"
                                    >{{ editButtonLabel }}</b-button
                                >
                            </router-link>
                        </template>
                        <template
                            v-if="useIsActiveCheckbox"
                            v-slot:cell(isActive)="{ value, item, field }"
                        >
                            <div class="d-flex justify-content-center">
                                <b-form-checkbox
                                    v-model="item.isActive"
                                    :disabled="field.disabled"
                                    name="isActive"
                                    @change="isActiveChanged(item)">
                                </b-form-checkbox>
                            </div>
                        </template>
                        <template
                            v-for="(_, name) in $slots" :key="name"
                            v-slot:[name]="slotData"
                        >
                            <slot :name="name" v-bind="slotData" />
                        </template>
                    </p-search-table>
                </div>
            </b-col>

            <b-col v-if="hasFilterSlot" cols="12" sm="3">
                <slot name="filters"></slot>
            </b-col>
        </b-row>
    </div>
</template>

<script>
// See documentation at https://bootstrap-vue.js.org/docs/components/table

import axios from 'axios';
import { searchPlaceHolderFormatter } from './Common/Formatters';

export default {
    name: 'p-search-screen',
    emits: ['selected', 'selected-rows-changed', 'total-rows-changed', 'is-table-loading', 'isActiveChanged'],
    props: {
        title: String,
        filters: Object,
        fields: Array,
        filterBy: Array,
        apiDestination: String,
        editKey: String,
        idKey: String,
        editUrlFunc: Function,
        addUrl: String,
        hideSearch: Boolean,
        addNewButton: Boolean,
        componentIsLoading: Boolean,
        editButtonLabel: {
            type: String,
            default: "Edit"
        },
        perPageOptions: {
            type: Array
        },
        perPage: {
            type: Number,
            default: 100
        },
        addBulkIsActiveToggle: {
            type: Boolean,
            default: false
        },
        showTopPaging: {
            type: Boolean,
            default: true
        },
        showBottomPaging: {
            type: Boolean,
            default: true
        },        
        useBootstrapSelection: {
            type: Boolean,
            default: true
        },
        disableInitialSearch: {
            type: Boolean,
            default: false
        },
        selectionActions: {
            type: Array,
            default: () => []
        },
        searchDestination: {
            type: String,
            default: null
        },
        showSetTo: {
            type: Boolean,
            default: true
        },
        useIsActiveCheckbox: {
            type: Boolean,
            default: true
        },
        submitSearchAsPost: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            selectedRows: [],
            defaultOption: 'All',
        };
    },
    computed: {
        computedSelectionActions() {
            //bulk IsActive toggle will be a common action, so we'll build it into search screen
            if (!this.addBulkIsActiveToggle) {
                return this.selectionActions || [];
            }
            return [
                {
                    label: 'Set to Active',
                    action: () => this.updateIsActiveStatus(true),
                    hidden: !this.selectedRows.some(x => !x.isActive)
                },
                {
                    label: 'Set to Inactive',
                    action: () => this.updateIsActiveStatus(false),
                    hidden: !this.selectedRows.some(x => x.isActive)
                },
                ...(this.selectionActions || [])
            ];
        },
        hasFilterSlot() {
            return !!this.$slots.filters;
        },
        showEdit() {
            return !!this.editKey || !!this.editUrlFunc;
        },
        allFields() {
            if (!this.showEdit) {
                return (this.fields || []).filter(x => !x.hidden);
            }
            return [{ key: 'edit', class: 'tcw100' }].concat(
                (this.fields || []).filter(x => !x.hidden)
            );
        },
        searchMessageComputed() {
            return searchPlaceHolderFormatter(this.filterBy || [],this.filters?.searchBy);
        }
    },
    watch: {
        selectedRows: {
            handler(rows) {
                this.$emit('selected', rows);
            },
            deep: true
        }
    },
    methods: {
        getQueryParameters() {
            return this.$refs.searchTable.getQueryParameters();
        },
        updateIsActiveStatus(isActive) {
            let dto = {
                isActive: isActive
            };
            dto[`${this.idKey ?? this.editKey}s`] = this.selectedRows.map(
                x => x[this.idKey ?? this.editKey]
            );
            axios.post(`${this.apiDestination}/setisactive`, dto).then(() => {
                //Not auto refreshing the search results.  That loses the seleciton.
                //Also, if you were sorting by is active, the items you were looking at would move,
                //possibly off the page.

                //This could cause confusion
                //and not allow the user to easily undo a bulk action they just did.
                this.selectedRows.forEach(x => (x.isActive = isActive));
                var msg = `${isActive ? 'Activated' : 'De-activated'} ${
                    dto[`${this.idKey ?? this.editKey}s`].length
                } record(s) successfully`;
                this.$appSuccess(msg);
            });
        },
        rowSelected(rows) {
            this.selectedRows = rows;
            this.$emit('selected', rows);
        },
        perPageUpdate(value) {
            this.filters.perPage = value;
        },
        sortByUpdate(value) {
            this.filters.sortBy = value;
        },
        sortDescUpdate(value) {
            this.filters.sortDesc = value;
        },
        editDestination(item) {
            if (this.editUrlFunc) {
                return this.editUrlFunc(item);
            }
            return `/${this.apiDestination}/${item[this.editKey]}`;
        },
        addDestination() {
            if (this.addUrl) {
                return this.addUrl;
            }
            return `/${this.apiDestination}/new`;
        },
        isActiveChanged(item) {
            this.$emit('isActiveChanged', item);
        },
        refreshSearchResults() {
            this.$refs.searchTable.refreshSearchResults();
        },
        clearSelectedRows() {
            this.$refs.searchTable.clearSelectedRows();
        }
    },
    async beforeMount() {
        if(this.$refs.searchRef)
        {
            this.$refs.searchRef.select();
        }
    }
};
</script>

<style scoped lang="scss">
@import '@/styles/app/common/variables.scss';
.search-title {
    margin-bottom: 30px;
}

.search-filters {
    margin-bottom: 10px;
}

:deep(.rounded-radio-group > label:first-child ) {
    border-top-left-radius: 7px;
    border-bottom-left-radius: 7px;
}
:deep(.input-group-append-slot) {
    display: flex;
    align-items: center;
    background-color: white;
    border: 1px solid $input-default-border-color;
    border-style: solid solid solid none;
    padding-right: 5px;
}
:deep(.form-control:focus ~ .input-group-append-slot) {
    border-color: $input-focus-border-color;
}
:deep(.rounded-radio-group > label:last-child ) {
    border-top-right-radius: 7px;
    border-bottom-right-radius: 7px;
}
:deep(.rounded-radio-group) {
    margin-top: 8px;
}
:deep(.rounded-radio-group > label) {
    padding-bottom: 0px;
}

:deep(.table > tbody > tr > td ) {
    vertical-align: middle;
}
:deep(.tcw100 ) {
    width: 100px;
}

.search-filter :deep(.form-select) {
    height: 43px;
}
.search-by-wrapper {
    border: 1px solid $input-default-border-color;
    border-radius: 0;
    overflow: visible;
    display: flex;
    align-self: stretch;
    flex: 0 0 auto;
    width: auto;
}
.search-by-wrapper :deep(.form-select) {
    border: none !important;
    border-radius: 0;
    height: 100%;
}
.search-by-wrapper :deep(.form-group),
.search-by-wrapper :deep(.mb-3) {
    margin-bottom: 0 !important;
    height: 100%;
    display: flex;
    align-items: stretch;
}
.search-by-wrapper :deep(label) {
    display: none;
}
.search-by-wrapper :deep(div) {
    height: 100%;
    display: flex;
    align-items: stretch;
}
.search-by-select {
    min-width: 160px !important;
    width: auto !important;
}
.search-by-select :deep(.form-select) {
    height: 43px;
    width: auto !important;
    min-width: 160px !important;
    padding-right: 2.5rem !important;
}
.search-by-select :deep(.form-group),
.search-by-select :deep(.mb-3) {
    margin-bottom: 0 !important;
}
</style>

<style>
/* Unscoped: belowSearch slot — force row children to be auto-width */
.below-search-slot {
    /* Top spacing intentionally omitted — when the search bar is visible its
       own .search-filters margin-bottom supplies the breathing room. When the
       search bar is hidden via hide-search, this slot sits flush under the
       action buttons row instead of leaving an empty band. */
    padding: 0 0 0.25rem 0;
}
.below-search-slot .row > * {
    width: auto !important;
    flex: 0 0 auto !important;
    padding-left: 0 !important;
}
.below-search-slot .row {
    gap: 0.75rem;
    margin-left: 0;
    align-items: center;
    --bs-gutter-x: 0;
}
/* p-checkbox defaults to block-level flex so columns of checkboxes stack;
   in filter-row slots (belowSearch, the Set To row containing addButton) we
   want them flowing horizontally alongside the other filter controls. */
.below-search-slot .p-checkbox-wrapper,
.set-to-slot .p-checkbox-wrapper {
    display: inline-flex;
}

/* Unscoped: actionButtons slot — flatten rows so buttons lay out horizontally */
.action-buttons-slot .row {
    display: contents !important;
}
.action-buttons-slot .row > * {
    width: auto !important;
    flex: 0 0 auto !important;
}
</style>
